import { forwardRef, useId } from 'react';
import { cn } from '../utils/cn';
import { Icon } from './Icon';

export interface FieldProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  label: string;
  /** Hide the label visually but keep it for assistive technology. */
  hideLabel?: boolean;
  hint?: string;
  error?: string | null;
  required?: boolean;
  children: (ids: {
    controlId: string;
    describedBy: string | undefined;
    invalid: boolean;
  }) => React.ReactNode;
}

/**
 * Label + hint + error wrapper shared by every form control, so that the
 * `aria-describedby` wiring is written once and cannot drift between controls.
 */
export const Field = forwardRef<HTMLDivElement, FieldProps>(function Field(
  { label, hideLabel, hint, error, required, className, children, ...rest },
  ref,
) {
  const base = useId();
  const controlId = `${base}-control`;
  const hintId = hint ? `${base}-hint` : undefined;
  const errorId = error ? `${base}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div ref={ref} className={cn('flex flex-col gap-1.5', className)} {...rest}>
      <label
        htmlFor={controlId}
        className={cn('text-dense-lg font-medium text-content-secondary', hideLabel && 'sr-only')}
      >
        {label}
        {required ? (
          <span className="ml-1 text-danger" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>
      {children({ controlId, describedBy, invalid: Boolean(error) })}
      {hint && !error ? (
        <p id={hintId} className="text-dense text-content-muted">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="flex items-center gap-1 text-dense text-danger">
          <Icon name="alert-circle" size={13} />
          {error}
        </p>
      ) : null}
    </div>
  );
});

export const controlBaseClasses =
  'w-full bg-surface text-body text-content border border-line rounded-md transition-colors duration-fast ease-standard placeholder:text-content-muted hover:border-line-strong disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-content-disabled aria-[invalid=true]:border-danger';
